import { useState, useRef, useEffect, useCallback } from "react";
import { Send, AlertTriangle, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useChat } from "@/hooks/useChat";
import { useFCMToken } from "@/hooks/useFCMToken";
import { ChatBubble } from "./ChatBubble";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

interface ChatRoomProps {
  chatId: string;
  currentUserId: string;
  onMarkAsRead?: () => void;
}

export function ChatRoom({ chatId, currentUserId, onMarkAsRead }: ChatRoomProps) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Define scrollToBottom before useChat to avoid reference errors
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const {
    messages,
    loading,
    error,
    sendMessage,
    pendingCount,
    connectionStatus,
    sseConnected,
  } = useChat({
    chatId,
    enabled: true,
    onNewMessage: () => {
      // 새 메시지 도착 시 스크롤
      scrollToBottom();
    },
  });

  // Register FCM token for push notifications
  const { token: fcmToken, error: fcmError } = useFCMToken({
    autoRegister: true,
  });

  useEffect(() => {
    if (fcmToken) {
      console.log("[ChatRoom] FCM token registered:", fcmToken.substring(0, 20) + "...");
    }
    if (fcmError) {
      console.warn("[ChatRoom] FCM error:", fcmError);
    }
  }, [fcmToken, fcmError]);

  useEffect(() => {
    scrollToBottom();
    // Mark as read when new messages arrive and user is viewing
    if (messages.length > 0 && onMarkAsRead) {
      // Debounce mark as read to avoid too many API calls
      const timer = setTimeout(() => {
        onMarkAsRead();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [messages, onMarkAsRead]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    const text = input.trim();
    setInput("");
    setSending(true);

    try {
      await sendMessage(text);
      inputRef.current?.focus();
    } catch (err) {
      console.error("Failed to send message:", err);
      // Phase 10: Show user-friendly error message
      const errorMessage = err instanceof Error ? err.message : "메시지 전송에 실패했습니다.";
      if (errorMessage.includes("Network") || errorMessage.includes("connection")) {
        // Network error - message is queued, show success message
        // Input is already cleared, so user can continue
      } else {
        // Other errors - restore input
        setInput(text);
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* 연결 상태 표시 */}
      {connectionStatus === "sse" && sseConnected && (
        <div className="px-4 py-1.5 bg-green-50 dark:bg-green-950/20 border-b border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 text-[11px] text-green-700 dark:text-green-300">
            <Wifi className="w-3 h-3" />
            <span>실시간 연결됨</span>
          </div>
        </div>
      )}
      {connectionStatus === "polling" && (
        <div className="px-4 py-1.5 bg-blue-50 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 text-[11px] text-blue-700 dark:text-blue-300">
            <WifiOff className="w-3 h-3" />
            <span>폴링 모드 (자동 재연결 시도 중...)</span>
          </div>
        </div>
      )}

      {/* 안전 배너 */}
      <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-2 text-[12px] text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">안전 거래 안내</p>
            <p className="mt-0.5">
              외부 결제 링크나 전화번호는 자동으로 가려집니다. 거래 확정 전까지는
              내장 채팅만 사용하세요.
            </p>
          </div>
        </div>
      </div>

      {/* 메시지 영역 */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {loading && messages.length === 0 ? (
          <div className="text-center text-ink-3 text-sm py-8">
            메시지를 불러오는 중...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-ink-3 text-sm py-8">
            메시지가 없습니다. 첫 메시지를 보내보세요!
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === currentUserId;
            const timeAgo = formatDistanceToNow(new Date(msg.created_at), {
              addSuffix: true,
              locale: ko,
            });

            return (
              <ChatBubble
                key={msg.id}
                me={isMe}
                text={msg.body}
                masked={msg.has_masked_link}
                time={timeAgo}
              />
            );
          })
        )}
        {error && (
          <div className="text-center text-red-500 text-sm py-2">{error}</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 입력 영역 */}
      <div className="border-t border-line-subtle p-4 bg-surface-0">
        {/* Phase 10: Show pending messages indicator */}
        {pendingCount > 0 && (
          <div className="mb-2 px-2 py-1 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200">
            {pendingCount}개의 메시지가 전송 대기 중입니다. 연결이 복구되면 자동으로 전송됩니다.
          </div>
        )}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="메시지를 입력하세요..."
            disabled={sending}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            size="icon"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

